/**
 * Topic display overrides — temporary client-side layer.
 *
 * INVARIANT — this map is DISPLAY ONLY:
 *   - NEVER pass the short name to an API call, cache key, URL, or setState
 *     for `selectedTopic`. The full DB topic string remains the identifier.
 *   - `displayTopic()` is only used at the point of rendering text to the user.
 *
 * Retire this file once the `topic_display` DB table + tutor-portal editor
 * ships. The bot has an equivalent map at
 * telegram-bot/bot_utils/topic_display.py — keep them in sync.
 *
 * Keys must match exact `question_metadata.topic` strings from the DB.
 * Short names should be ≤ 22 chars so they fit Telegram inline buttons too.
 *
 * Reviewed by Jojo on 2026-04-15. Subjects intentionally left as-is
 * (no overrides): Chemistry, Mathematics, Economics, Biology.
 */

export const TOPIC_DISPLAY_NAMES: Record<string, string> = {
  // --- Geography ---
  "Atmosphere Weather and Climate": "Weather & Climate",
  "Geographic Skills and Methods": "Skills & Methods",
  "Population Geography": "Population",
  "Regional Geography Africa": "Africa",
  "Economic Geography and Development": "Economy & Development",
  "Physical Geography and Geology": "Physical Geography",
  "Regional Geography Ethiopia": "Ethiopia",
  "Natural Resources and Management": "Natural Resources",
  "Environmental Issues and Sustainability": "Environment",
  "Foundations of Geography": "Foundations",
  "Ecosystems and Biogeography": "Ecosystems",
  "Settlement Geography": "Settlement",

  // --- Physics ---
  "Magnetism and Electromagnetism": "Electromagnetism",
  "Rotational Motion and Gravitation": "Rotation & Gravitation",
  "Work, Energy, Power, and Momentum": "Energy & Momentum",
  "Dynamics and Newton's Laws": "Newton's Laws",
  "Physical Quantities and Measurement": "Measurement",

  // --- History ---
  "Ethiopia - Modern Political and Social Developments": "Modern Ethiopia",
  "Ethiopia - Medieval and Early Modern States": "Medieval Ethiopia",
  "Ethiopia and the Horn - Early Period": "Early Ethiopia",
  "The Middle Ages and Early Modern World": "Middle Ages",
  "Global and Regional Developments Since 1945": "Global and Regional - Since 1945",
  "Ethiopia - 16th to 19th Century Political Processes": "Ethiopia - 16th to 19th Century",
  "Society and Politics in the Age of World Wars (1914-1945)": "World Wars (1914-1945)",
  "Ancient World Civilizations": "Ancient Civilizations",
  "Africa and the Colonial Experience (1880s-1960s)": "Colonial Africa (1880s-1960s)",
  "Human Evolution and Early Development": "Human Evolution",
  "History and Historiography": "Historiography",
  "Peoples and States of Africa to 1500": "Early Africa - to 1500",
  "Indigenous Knowledge Systems and Heritages of Ethiopia": "Ethiopia - Indigenous Knowledge",

  // --- Civics ---
  "Work Ethics and Self-Reliance": "Work Ethics",
  "Rule of Law and Justice": "Law & Justice",
  "Citizenship and Civic Responsibility": "Citizenship",
  "Economic Principles and Development": "Economy & Development",
  "Civic Participation and Leadership": "Civic Participation",
  "Patriotism and National Identity": "Patriotism & Identity",
  "International Relations and Cooperation": "International Relations",
  "Human Rights and Protection": "Human Rights",
  "Conflict Resolution and Peace": "Conflict & Peace",

  // --- English ---
  "Communication and Dialogue": "Communication",
  "Vocabulary and Word Skills": "Vocabulary",
  "Grammar - Sentence Structure": "Sentence Structure",
  "Grammar - Tenses and Verb Forms": "Tenses & Verbs",
  "Writing and Composition": "Writing",
  "Language Usage and Correction": "Usage & Correction",

  // --- Aptitude ---
  "Vocabulary and Language": "Vocabulary",
  "Algebraic Problem Solving": "Algebra",
  "Probability and Statistics": "Probability & Stats",
  "Applied Problem Solving": "Applied Problems",
  "Spatial and Geometric Reasoning": "Spatial & Geometric",
};

export function displayTopic(topic: string | null | undefined): string {
  if (!topic) return "";
  return TOPIC_DISPLAY_NAMES[topic] ?? topic;
}
